import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  Alert,
  alpha,
  useTheme,
  Select,
  MenuItem,
  SelectChangeEvent,
  LinearProgress,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Search, TrendingUp, AccessTime, Home } from '@mui/icons-material';
import marketDataService, {
  MarketData as MarketDataType,
  BuyerAnalysis,
} from '../../api/services/market-data';
import useIsMobile from '../../hooks/useIsMobile';

interface MarketTrend {
  direction: 'improving_for_buyers' | 'stable' | 'improving_for_sellers';
  salesChange: number;
  daysOnMarketChange: number;
  priceDropChange: number;
  periodWeeks: number;
  confidence: 'high' | 'medium' | 'low';
}

interface MarketData {
  monthsOfInventory: number;
  medianDaysOnMarket: number;
  medianDaysToContract: number;
  marketCondition: string;
  listToSaleRatio: number;
  medianSoldPrice: number;
  medianListPrice: number;
  priceDropPercent: number;
  activeListings: number;
  underContractPercent: number;
  trend: MarketTrend | null;
  zipCode: string;
  lastUpdated: string;
  buyerAnalysis?: BuyerAnalysis;
}

interface MarketAnalysisProps {
  zip?: string;
  dom?: number;
  listingPrice?: number;
  area?: number;
  /**
   * Coordinates/state/MLS property type from the listing's own record, when
   * a caller has one (PropertyInfoDialog does). When both `lat` and `lng`
   * are usable, the market read scopes to a radius around this home -- the
   * same population the comps engine draws from -- rather than the whole
   * ZIP a wide municipal boundary can span. Omit any of these (as the
   * standalone zip-search full-mode UI does) to keep the existing ZIP-only
   * lookup.
   */
  lat?: number;
  lng?: number;
  state?: string;
  /** MLS `propertyType` enum value (e.g. 'SFR'), not the display string. */
  propertyType?: string;
  /** MLS `isActive`/status of this specific listing (e.g. 'ACTIVE', 'SOLD'). */
  status?: string;
  compact?: boolean;
  onDataLoaded?: (data: MarketData) => void;
  onLoadingChange?: (loading: boolean) => void;
}

const MarketHealthGauge: React.FC<{
  value: number;
  maxValue: number;
  title: string;
  definition: string;
  unit: string;
  icon: React.ReactNode;
  thresholds: {
    good: number;
    poor: number;
  };
  inverse?: boolean; // true if lower values are better
  educationalInfo: string;
  whyItMatters: string;
  isMobile?: boolean;
}> = ({
  value,
  maxValue,
  title,
  definition,
  unit,
  icon,
  thresholds,
  inverse = false,
  educationalInfo,
  whyItMatters,
  isMobile = false,
}) => {
  const theme = useTheme();

  // Calculate percentage for the gauge
  const percentage = Math.min((value / maxValue) * 100, 100);

  // Determine color based on thresholds
  const getColor = () => {
    if (inverse) {
      // For metrics where lower is better for sellers (like days on market)
      // Higher values = buyer favorable, lower values = seller favorable
      if (value >= thresholds.poor) return 'success'; // High DOM = buyer market
      if (value <= thresholds.good) return 'error'; // Low DOM = seller market
      return 'warning';
    } else {
      // For metrics like months of inventory
      // Higher values = buyer favorable, lower values = seller favorable
      if (value >= thresholds.poor) return 'success'; // High inventory = buyer market
      if (value <= thresholds.good) return 'error'; // Low inventory = seller market
      return 'warning';
    }
  };

  const color = getColor();
  const colorValue = theme.palette[color].main;

  // Create interpretation text
  const getInterpretation = () => {
    const colorName =
      color === 'success'
        ? 'Buyer Market'
        : color === 'warning'
        ? 'Balanced Market'
        : 'Seller Market';
    return colorName;
  };

  return (
    <Paper sx={{ p: isMobile ? 2 : 3, textAlign: 'center' }} variant="outlined">
      <Stack spacing={2} alignItems="center">
        {/* Header with title and info tooltip */}
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Stack>

        {/* Definition text */}
        <Typography
          variant="body1"
          sx={{ fontSize: '1rem', fontStyle: 'italic' }}
        >
          {definition}
        </Typography>

        {/* Speedometer-style gauge */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {/* Background circle */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={isMobile ? 120 : 140}
            thickness={6}
            sx={{
              color: alpha(theme.palette.grey[300], 0.3),
              position: 'absolute',
            }}
          />
          {/* Value circle */}
          <CircularProgress
            variant="determinate"
            value={percentage}
            size={isMobile ? 120 : 140}
            thickness={6}
            sx={{
              color: colorValue,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          {/* Center value */}
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="h4"
              component="div"
              fontWeight={700}
              color={colorValue}
            >
              {value}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {unit}
            </Typography>
          </Box>
        </Box>

        <Chip
          label={getInterpretation()}
          color={color}
          size="medium"
          sx={{ fontWeight: 600, fontSize: '1rem', py: 1 }}
        />

        {/* Educational Information */}
        <Box sx={{ width: '100%', textAlign: 'left' }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: '1rem', mb: 1 }}
          >
            {educationalInfo}
          </Typography>
        </Box>

        {/* Why This Matters Section */}
        <Box
          sx={{
            width: '100%',
            textAlign: 'left',
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="subtitle1"
            color="primary.main"
            sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}
          >
            💡 Why This Matters:
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: '1rem' }}
          >
            {whyItMatters}
          </Typography>
        </Box>

        {/* Threshold indicators */}
        <Box sx={{ width: '100%', mt: 1 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              <Typography variant="body2" color="error.main" fontWeight={600}>
                {'<'} {thresholds.good} {unit}
              </Typography>
              <Typography
                variant="body2"
                display="block"
                color="text.secondary"
              >
                Limited Opportunities
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flex: 1 }}>
              <Typography variant="body2" color="success.main" fontWeight={600}>
                {'>'} {thresholds.poor} {unit}
              </Typography>
              <Typography
                variant="body2"
                display="block"
                color="text.secondary"
              >
                Strong Opportunities
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export const MarketAnalysis: React.FC<MarketAnalysisProps> = ({
  zip: propZip,
  dom: propDom,
  listingPrice: propListingPrice,
  area: propArea,
  lat: propLat,
  lng: propLng,
  state: propState,
  propertyType: propMlsPropertyType,
  status: propStatus,
  compact = false,
  onDataLoaded,
  onLoadingChange,
}) => {
  const [inputZipCode, setInputZipCode] = useState(propZip || '');
  const [propertyType, setPropertyType] = useState('Single Family');
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Auto-fetch when zip prop is provided (embedded mode)
  useEffect(() => {
    if (propZip) {
      setInputZipCode(propZip);
      fetchMarketData(propZip);
    }
  }, [
    propZip,
    propDom,
    propListingPrice,
    propArea,
    propLat,
    propLng,
    propState,
    propMlsPropertyType,
    propStatus,
  ]);

  const fetchMarketData = async (zipCode: string) => {
    setLoading(true);
    setError('');

    try {
      // A radius center needs both coordinates; zero is the schema default
      // for a listing the feed never geocoded, not a real point, so it does
      // not count as usable either. Without one, this keeps sending the
      // ZIP-only lookup it always has -- the standalone full-mode search
      // (a user-typed ZIP with no listing behind it) has no coordinates to
      // offer.
      const hasRadiusCenter =
        propLat !== undefined && propLat !== 0 && propLng !== undefined && propLng !== 0;

      const response = await marketDataService.find({
        params: {
          zip: zipCode,
          propertyType,
          includeBuyerAnalysis: propDom !== undefined,
          dom: propDom,
          listingPrice: propListingPrice,
          area: propArea,
          status: propStatus,
          ...(hasRadiusCenter
            ? {
                scope: 'radius' as const,
                lat: propLat,
                lng: propLng,
                state: propState,
                mlsPropertyType: propMlsPropertyType,
              }
            : {}),
        },
      });

      const mappedData: MarketData = {
        monthsOfInventory: Math.round(response.monthsOfInventory * 10) / 10,
        medianDaysOnMarket: Math.round(response.medianDaysOnMarket),
        medianDaysToContract: Math.round(response.medianDaysToContract),
        marketCondition: response.marketCondition,
        listToSaleRatio: response.listToSaleRatio,
        medianSoldPrice: response.medianSoldPrice,
        medianListPrice: response.medianListPrice,
        priceDropPercent: response.priceDropPercent,
        activeListings: response.activeListings,
        underContractPercent: response.underContractPercent,
        trend: response.trend,
        zipCode: zipCode,
        lastUpdated: new Date().toLocaleDateString(),
        buyerAnalysis: response.buyerAnalysis,
      };

      setMarketData(mappedData);
      onDataLoaded?.(mappedData);
    } catch (err) {
      setError('Failed to fetch market data. Please try again.');
      console.error('Market data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Validate zip code (must be exactly 5 digits)
  const isValidZipCode = /^\d{5}$/.test(inputZipCode.trim());

  const handleSearch = async () => {
    if (!isValidZipCode) return;
    await fetchMarketData(inputZipCode);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isValidZipCode) {
      handleSearch();
    }
  };

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numeric input and limit to 5 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setInputZipCode(value);
  };

  const handlePropertyTypeChange = (e: SelectChangeEvent<string>) => {
    setPropertyType(e.target.value);
  };

  // Calculate base market health score from market condition
  const calculateMarketHealthScore = (data: MarketData): number => {
    const conditionScores: Record<string, number> = {
      "Buyer's Market": 75,
      'Balanced Market': 50,
      "Seller's Market": 25,
      'Insufficient Data': 50,
    };
    return conditionScores[data.marketCondition] ?? 50;
  };

  // Calculate property-specific bargaining score (0-100)
  const calculateBargainingScore = (data: MarketData, dom?: number): number => {
    let score = calculateMarketHealthScore(data);

    // DOM bonus: property on market longer than median
    if (dom !== undefined && data.medianDaysOnMarket > 0) {
      const domRatio = dom / data.medianDaysOnMarket;
      if (domRatio > 2) score += 15;
      else if (domRatio > 1.5) score += 10;
      else if (domRatio < 0.5) score -= 10;
    }

    // List-to-sale ratio bonus
    if (data.listToSaleRatio > 0) {
      if (data.listToSaleRatio < 97) score += 10;
      else if (data.listToSaleRatio >= 100) score -= 10;
    }

    return Math.max(0, Math.min(100, score));
  };

  // Get expressive label for bargaining power
  const getExpressiveLabel = (
    score: number,
    buyerAnalysis?: BuyerAnalysis
  ): string => {
    if (buyerAnalysis?.offerRecommendation) {
      return buyerAnalysis.offerRecommendation;
    }

    if (score >= 70) return 'Strong Negotiating Position';
    if (score >= 50) return 'Room to Negotiate';
    if (score >= 30) return 'Balanced Position';
    return 'Competitive Market';
  };

  // Get score theme with colors and labels
  const getScoreTheme = (score: number) => {
    if (score >= 70) {
      return {
        main: '#10b981', // Emerald green
        light: '#d1fae5', // Very light green
        dark: '#059669', // Dark green
        label: 'Strong Position',
        chipColor: 'success' as const,
      };
    }
    if (score >= 50) {
      return {
        main: '#3b82f6', // Blue
        light: '#dbeafe', // Light blue
        dark: '#1e40af', // Dark blue
        label: 'Room to Negotiate',
        chipColor: 'info' as const,
      };
    }
    if (score >= 30) {
      return {
        main: '#f59e0b', // Amber
        light: '#fef3c7', // Light amber
        dark: '#d97706', // Dark amber
        label: 'Some Negotiation Room',
        chipColor: 'warning' as const,
      };
    }
    return {
      main: '#f43f5e', // Rose
      light: '#ffe4e6', // Light rose
      dark: '#be123c', // Dark rose
      label: 'Competitive Market',
      chipColor: 'error' as const,
    };
  };

  // Get chip color based on score (for legacy usage)
  const getScoreColor = (
    score: number
  ): 'success' | 'info' | 'warning' | 'error' => {
    if (score >= 70) return 'success';
    if (score >= 50) return 'info';
    if (score >= 30) return 'warning';
    return 'error';
  };

  // Use backend score if available, otherwise calculate locally
  const bargainingScore =
    marketData?.buyerAnalysis?.bargainingScore?.score ??
    (marketData ? calculateBargainingScore(marketData, propDom) : 0);
  const marketHealthScore = marketData
    ? calculateMarketHealthScore(marketData)
    : 0;
  const scoreConfidence =
    marketData?.buyerAnalysis?.bargainingScore?.confidence ?? 'medium';

  // Explanation of the combined metric
  const combinedMetricExplanation =
    'This index shows how much negotiating power buyers have. Higher numbers mean more opportunities to find deals.';

  const theme = useTheme();
  const isMobile = useIsMobile();

  // Compact mode render for PropertyInfoDialog
  if (compact) {
    // Loading state
    if (loading) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Analyzing market...
          </Typography>
        </Box>
      );
    }

    // Error state - hide the component entirely
    if (error) {
      return null;
    }

    // No data yet
    if (!marketData) {
      return null;
    }

    const scoreTheme = getScoreTheme(bargainingScore);

    // Calculate DOM comparison
    const medianDOM =
      marketData.medianDaysToContract || marketData.medianDaysOnMarket;
    const domDiff = propDom && medianDOM ? propDom - medianDOM : null;
    const domStatus =
      domDiff !== null
        ? domDiff > 14
          ? 'stale'
          : domDiff < -7
          ? 'fresh'
          : 'normal'
        : null;

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 2,
          bgcolor: '#fff',
        }}
      >
        {/* Header: Title + Status Chip */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              columnGap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.primary"
              sx={{ letterSpacing: 0.25 }}
            >
              Bargaining Power
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#9ca3af', fontSize: '10px' }}
            >
              Provided by Homepoint
            </Typography>
          </Box>
          <Chip
            label={scoreTheme.label}
            size="small"
            sx={{
              bgcolor: scoreTheme.main,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 26,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        </Stack>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={bargainingScore}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.grey[400], 0.15),
              '& .MuiLinearProgress-bar': {
                bgcolor: scoreTheme.main,
                borderRadius: 4,
                transition: 'transform 0.4s ease',
              },
            }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: 'block',
              fontSize: '0.6875rem',
            }}
          >
            {bargainingScore}/100 negotiating strength
          </Typography>
        </Box>

        {/* Offer Recommendation */}
        {marketData.buyerAnalysis?.offerRange && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: alpha(scoreTheme.main, 0.08),
              borderRadius: 1.5,
              borderLeft: `3px solid ${scoreTheme.main}`,
            }}
          >
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {marketData.buyerAnalysis.offerRange}
            </Typography>
          </Box>
        )}

        {/* Market Insights */}
        <Stack spacing={1}>
          {marketData.listToSaleRatio > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor:
                    marketData.listToSaleRatio >= 100
                      ? theme.palette.error.main
                      : theme.palette.success.main,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                Homes here sell for{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color:
                      marketData.listToSaleRatio >= 100
                        ? theme.palette.error.main
                        : theme.palette.success.main,
                  }}
                >
                  {marketData.listToSaleRatio.toFixed(0)}%
                </Box>{' '}
                of asking price
              </Typography>
            </Box>
          )}
          {propDom !== undefined && medianDOM > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor:
                    domStatus === 'stale'
                      ? theme.palette.success.main
                      : domStatus === 'fresh'
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                This home has been listed{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color:
                      domStatus === 'stale'
                        ? theme.palette.success.main
                        : domStatus === 'fresh'
                        ? theme.palette.error.main
                        : theme.palette.text.primary,
                  }}
                >
                  {propDom} days
                </Box>{' '}
                (avg: {medianDOM})
              </Typography>
            </Box>
          )}
          {marketData.underContractPercent > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor:
                    marketData.underContractPercent > 30
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6 }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color:
                      marketData.underContractPercent > 30
                        ? theme.palette.error.main
                        : theme.palette.text.primary,
                  }}
                >
                  {marketData.underContractPercent.toFixed(0)}%
                </Box>{' '}
                of listings already under contract
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Next Steps */}
        {marketData.buyerAnalysis?.nextSteps &&
          marketData.buyerAnalysis.nextSteps.length > 0 && (
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Next Steps
              </Typography>
              <Stack spacing={0.5}>
                {marketData.buyerAnalysis.nextSteps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        flexShrink: 0,
                        display: 'inline-block',
                        position: 'relative',
                        top: -2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1.5,
                  color: 'text.disabled',
                  fontStyle: 'italic',
                }}
              >
                For informational purposes. Your Homepoint expert can provide
                personalized guidance.
              </Typography>
            </Box>
          )}
      </Paper>
    );
  }

  // Full mode (original) render
  return (
    <Box>
      <Stack spacing={3}>
        {/* Search Section */}
        <Stack spacing={2}>
          <Typography variant="body1">
            Enter your zip code to see if it&apos;s a good time to buy in your
            area
          </Typography>

          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems={isMobile ? 'stretch' : 'center'}
          >
            <TextField
              label="Zip Code"
              value={inputZipCode}
              onChange={handleZipCodeChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter 5-digit zip code"
              size="small"
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? undefined : 150 }}
              inputProps={{
                maxLength: 5,
                pattern: '[0-9]*',
              }}
            />
            <Select
              label="Property Type"
              value={propertyType}
              onChange={handlePropertyTypeChange}
              size="small"
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? undefined : 150 }}
            >
              <MenuItem value="Single Family">Single Family</MenuItem>
              <MenuItem value="Condo">Condo</MenuItem>
            </Select>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !isValidZipCode}
              startIcon={loading ? <CircularProgress size={16} /> : <Search />}
              fullWidth={isMobile}
              sx={{ py: isMobile ? 1.5 : undefined }}
            >
              {loading ? 'Checking...' : 'Check Market'}
            </Button>
          </Stack>
        </Stack>

        {/* Error Display */}
        {error && <Alert severity="error">{error}</Alert>}

        {/* Market Data Display */}
        {marketData && (
          <Box>
            {/* Expressive Bargaining Power Chip */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                bgcolor: alpha(
                  theme.palette[getScoreColor(bargainingScore)].main,
                  0.05
                ),
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Your Negotiating Position
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', fontSize: '10px' }}
                  >
                    Provided by Homepoint
                  </Typography>
                </Box>
                <Chip
                  label={`${getExpressiveLabel(
                    bargainingScore,
                    marketData.buyerAnalysis
                  )} ${bargainingScore}/100`}
                  color={getScoreColor(bargainingScore)}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={bargainingScore}
                color={getScoreColor(bargainingScore)}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: alpha(theme.palette.grey[300], 0.3),
                }}
              />
            </Paper>

            {/* Show market condition */}
            <Alert
              severity={
                marketData.marketCondition === "Buyer's Market"
                  ? 'success'
                  : marketData.marketCondition === "Seller's Market"
                  ? 'warning'
                  : 'info'
              }
              sx={{ mb: 2 }}
            >
              <Typography variant="body1" fontWeight={600}>
                Current Market: {marketData.marketCondition}
              </Typography>

              {/* Trend Indicator - only show if trending toward a different market condition */}
              {marketData.trend &&
                marketData.trend.direction !== 'stable' &&
                !(
                  marketData.marketCondition === "Buyer's Market" &&
                  marketData.trend.direction === 'improving_for_buyers'
                ) &&
                !(
                  marketData.marketCondition === "Seller's Market" &&
                  marketData.trend.direction === 'improving_for_sellers'
                ) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        color:
                          marketData.trend.direction === 'improving_for_buyers'
                            ? 'success.main'
                            : 'warning.main',
                        fontWeight: 600,
                      }}
                    >
                      {marketData.trend.direction === 'improving_for_buyers'
                        ? '↗'
                        : '↘'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          marketData.trend.direction === 'improving_for_buyers'
                            ? 'success.main'
                            : 'warning.main',
                        fontWeight: 500,
                      }}
                    >
                      {marketData.trend.direction === 'improving_for_buyers'
                        ? "Trending toward Buyer's Market"
                        : "Trending toward Seller's Market"}
                      {Math.abs(marketData.trend.daysOnMarketChange) > 1 &&
                        ` (homes selling ${
                          marketData.trend.daysOnMarketChange > 0
                            ? 'slower'
                            : 'faster'
                        } than a year ago, ${Math.abs(
                          marketData.trend.daysOnMarketChange
                        ).toFixed(0)}%)`}
                    </Typography>
                  </Box>
                )}

              <Typography variant="body2" sx={{ mt: 1 }}>
                {marketData.activeListings} active listings
              </Typography>
              {marketData.listToSaleRatio > 0 && (
                <Typography variant="body2">
                  Homes typically sell for{' '}
                  {marketData.listToSaleRatio.toFixed(1)}% of asking price
                </Typography>
              )}
              {marketData.medianSoldPrice > 0 && (
                <Typography variant="body2">
                  Median Sold Price: $
                  {marketData.medianSoldPrice.toLocaleString()}
                </Typography>
              )}
              {marketData.priceDropPercent > 0 && (
                <Typography variant="body2">
                  {marketData.priceDropPercent.toFixed(0)}% of listings have
                  dropped their price
                </Typography>
              )}
            </Alert>

            <MarketHealthGauge
              value={marketHealthScore}
              maxValue={100}
              title="Buyer Opportunity Index"
              definition="How many opportunities buyers have to negotiate"
              unit="%"
              icon={<TrendingUp color="primary" fontSize="large" />}
              thresholds={{
                good: 40,
                poor: 60,
              }}
              educationalInfo={combinedMetricExplanation}
              whyItMatters={
                'A higher index means more homes to choose from and more room to negotiate. ' +
                'A lower index means competition is fierce - be ready with strong offers.'
              }
              isMobile={isMobile}
            />

            {/* Individual metrics shown after combined score */}
            <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 600 }}>
              Market Details
            </Typography>
            <Grid2 container spacing={isMobile ? 2 : 3}>
              <Grid2 xs={12} sm={6}>
                <MarketHealthGauge
                  value={marketData.monthsOfInventory}
                  maxValue={12}
                  title="Homes Available"
                  definition="How many months it would take to sell all homes on the market"
                  unit="months"
                  icon={<Home color="primary" />}
                  thresholds={{
                    good: 4,
                    poor: 6,
                  }}
                  educationalInfo={
                    marketData.monthsOfInventory < 4
                      ? 'Few homes available - be ready to move fast on good properties'
                      : marketData.monthsOfInventory > 6
                      ? 'Lots of homes available - take your time to find the right one'
                      : 'Normal number of homes available - good balance for buyers'
                  }
                  whyItMatters={
                    'More homes means more choices and better deals. ' +
                    'Fewer homes means you need to be quick and ready to compete.'
                  }
                  isMobile={isMobile}
                />
              </Grid2>

              {marketData.medianDaysToContract > 0 && (
                <Grid2 xs={12} sm={6}>
                  <MarketHealthGauge
                    value={marketData.medianDaysToContract}
                    maxValue={120}
                    title="Time to Sell"
                    definition="How long until homes go under contract"
                    unit="days"
                    icon={<AccessTime color="primary" />}
                    thresholds={{
                      good: 30,
                      poor: 60,
                    }}
                    inverse={true}
                    educationalInfo={
                      marketData.medianDaysToContract < 30
                        ? 'Homes sell fast - have your financing ready and be prepared to act quickly'
                        : marketData.medianDaysToContract > 60
                        ? 'Homes stay on market longer - you have time to shop around and negotiate'
                        : 'Normal selling pace - good balance of time to decide'
                    }
                    whyItMatters={
                      'When homes sell fast, you need to be ready to move quickly. ' +
                      'When they stay longer, you have more time to find the right home.'
                    }
                    isMobile={isMobile}
                  />
                </Grid2>
              )}
            </Grid2>
          </Box>
        )}

        {/* Default state when no data */}
        {!marketData && !loading && !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }} variant="outlined">
            <Stack spacing={2} alignItems="center">
              <TrendingUp sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.secondary">
                Check your local market
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See if it&apos;s a good time to buy in your area
              </Typography>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default MarketAnalysis;
